import { PublicHeader } from '@/components/PublicHeader';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useHousehold } from '@/contexts/HouseholdContext';
import { householdApi } from '@/lib/api/household';
import { getInviteCode } from '@/lib/householdInvite';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

export function JoinHousehold() {
  const [params] = useSearchParams();
  const code = getInviteCode(params.get('code'));
  const { isAuthenticated, isLoading, user } = useAuth();
  const { refreshHousehold } = useHousehold();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    setLoading(true);
    setError('');
    try {
      await householdApi.joinHousehold(code);
      await refreshHousehold();
      navigate('/household', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to join this household.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page flex min-h-svh flex-col bg-background text-foreground">
      <PublicHeader
        actionTo={isAuthenticated ? '/dashboard' : `/login?invite=${code}`}
        actionLabel={isAuthenticated ? 'Dashboard' : 'Sign in'}
      />
      <main
        id="main-content"
        className="flex flex-1 items-center justify-center px-5 py-12"
      >
        <div className="w-full max-w-sm space-y-6">
          <h1 className="font-serif text-4xl font-normal tracking-tight">
            Join your household.
          </h1>
          {!code ? (
            <p role="alert">
              This invitation is incomplete. Ask your partner for a new
              invitation.
            </p>
          ) : isLoading ? (
            <p role="status">Loading your account…</p>
          ) : isAuthenticated ? (
            <>
              <p className="text-sm text-muted-foreground">
                Join with invitation code <strong>{code}</strong> as{' '}
                {user?.username}. You’ll be able to view accounts and budgets
                shared with this household.
              </p>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button onClick={accept} disabled={loading} className="w-full">
                {loading ? 'Joining…' : 'Accept invitation'}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Sign in or create an account to accept this invitation.
                Invitations expire after 48 hours.
              </p>
              <Button asChild className="w-full">
                <Link to={`/login?invite=${code}`}>Sign in to join</Link>
              </Button>
              <Link
                to={`/register?invite=${code}`}
                className="block text-center text-sm text-primary hover:underline"
              >
                Create an account
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
